import { useEffect, useState } from 'react';
import { fetchTickets, fetchSniper, fetchSniperMine } from '../api';
import { getPushPref, setPushPref } from '../alerts';
import { TicketsResponse, TradeTicket, SniperResponse, SniperSetup, SniperFire, SniperMineResponse } from '../types';

// 🎫 뉴 단타(07-09 전면 개편) — "정보 제공"이 아니라 "결정 제안": 시스템이 완성된 매매 제안서(탈것·진입·손절·목표·시간제한)을
// 내고 스스로 채점한다. 사용자 기입 불필요 — 매매 제안서 성적표가 신뢰를 숫자로 벌어야 함. ⚠️ 제안·참고용, 매매 지시 아님.

function fmtWon(v: number): string {
  return v.toLocaleString('ko-KR');
}

function TicketCard({ t, live }: { t: TradeTicket; live: boolean }) {
  const up = t.dir > 0;
  const [, tickr] = useState(0);
  useEffect(() => {
    if (!live) return;
    const i = setInterval(() => tickr((v) => v + 1), 1000);
    return () => clearInterval(i);
  }, [live]);
  const leftS = live ? Math.max(0, Math.floor(t.deadline_ts - Date.now() / 1000)) : 0;
  const pnl = live ? (t.cur_pnl ?? 0) : (t.pnl_pct ?? 0);
  const pnlCls = pnl > 0 ? 'text-red-300' : pnl < 0 ? 'text-blue-300' : 'text-gray-400';
  const badge = t.status === 'active' ? { txt: '진행 중', cls: 'bg-amber-500/20 text-amber-200 border-amber-500/50 animate-pulse' }
    : t.status === 'win' ? { txt: '✅ 목표/이익', cls: 'bg-green-500/15 text-green-300 border-green-500/40' }
    : t.status === 'loss' ? { txt: '❌ 손절/손해', cls: 'bg-red-500/15 text-red-300 border-red-500/40' }
    : { txt: '⏱ 시간초과(본전)', cls: 'bg-gray-600/30 text-gray-300 border-gray-600' };
  // 진행 바 — 손절(0%)~목표(100%) 사이 현재 위치
  const range = t.target_px - t.stop_px;
  const pos = Math.max(0, Math.min(100, (((live ? t.cur_px : t.exit_px) ?? t.entry_px) - t.stop_px) / range * 100));
  const entryPos = (t.entry_px - t.stop_px) / range * 100;
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${live ? 'border-amber-500/40 bg-amber-500/[0.04]' : 'border-gray-800 bg-gray-900/40'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-gray-500 font-mono text-[11px]">{t.hm}</span>
        <b className={`text-[13px] ${up ? 'text-red-200' : 'text-blue-200'}`}>{up ? '📈 상방' : '📉 하방'} — {t.inst_name}</b>
        <span className={`text-[10px] px-1.5 py-px rounded-full border font-medium ${badge.cls}`}>{badge.txt}</span>
        {live && <span className="text-[11px] text-gray-400 font-mono ml-auto">⏳ {Math.floor(leftS / 60)}:{String(leftS % 60).padStart(2, '0')}</span>}
        {!live && t.pnl_pct != null && <span className={`font-mono font-bold ml-auto ${pnlCls}`}>{t.pnl_pct > 0 ? '+' : ''}{t.pnl_pct}%</span>}
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-[11px] flex-wrap">
        <span className="text-gray-400">진입 <b className="text-gray-200 font-mono">{fmtWon(t.entry_px)}</b></span>
        <span className="text-blue-300/80">손절 <b className="font-mono">{fmtWon(t.stop_px)}</b></span>
        <span className="text-green-300/80">목표 <b className="font-mono">{fmtWon(t.target_px)}</b></span>
        {live && t.cur_px != null && <span className="text-gray-300">현재 <b className="font-mono">{fmtWon(t.cur_px)}</b> <b className={`font-mono ${pnlCls}`}>({pnl > 0 ? '+' : ''}{pnl}%)</b></span>}
      </div>
      {/* 손절~목표 진행 바 */}
      <div className="relative h-2 rounded bg-gray-800 mt-1.5 overflow-hidden" title="파란 끝=손절 · 초록 끝=목표 · 흰 선=진입가">
        <div className="absolute inset-y-0 left-0 w-1 bg-blue-400/70" />
        <div className="absolute inset-y-0 right-0 w-1 bg-green-400/70" />
        <div className="absolute inset-y-0 w-px bg-gray-300/70" style={{ left: `${entryPos}%` }} />
        <div className={`absolute inset-y-0 w-1.5 rounded ${pnl >= 0 ? 'bg-red-400' : 'bg-blue-300'}`} style={{ left: `calc(${pos}% - 3px)` }} />
      </div>
      <div className="mt-1.5 space-y-0.5">
        {(t.why || []).map((w, i) => <p key={i} className="text-[10.5px] text-gray-500 leading-snug">· {w}</p>)}
      </div>
      {!live && t.mfe != null && <p className="text-[10px] text-gray-600 mt-1">최대 이익 폭 +{t.mfe}% · 최대 손실 폭 {t.mae}%</p>}
    </div>
  );
}

// 🫳 행동 문장/배너 방향 라벨 — 초보자 일상어(인버스=하락 베팅, 레버리지=반등 베팅)
function dirAct(dir: number): string {
  return dir < 0 ? '인버스(하락 베팅)' : dir > 0 ? '레버리지(반등 베팅)' : '양방향';
}

// 🫳 행동 배너(07-20) — 같은 방향 발동이 2건↑ 겹치면 카드 대신 배너 1개로 "무엇을 하라"를 문장으로.
function ActionBanner({ dir, count, fire, sizeLabel }: { dir: number; count: number; fire: SniperFire; sizeLabel?: string }) {
  return (
    <div className="rounded-xl border-2 border-amber-400/80 bg-amber-500/[0.13] ring-1 ring-amber-400/40 px-3.5 py-3">
      <p className="text-[14px] font-bold text-amber-100">🫳 지금 {dirAct(dir)} 자리 — 신호 {count}개 겹침</p>
      <p className="text-[12px] text-amber-50/90 font-medium mt-1 leading-snug">
        진입 <b className="font-mono">{fmtWon(fire.entry_px ?? 0)}원</b> · 손절 <b className="font-mono">{fmtWon(fire.stop_px ?? 0)}</b>(−0.5%) · 목표 <b className="font-mono">{fmtWon(fire.target_px ?? 0)}</b>(+1%) · 30분 안 결판{sizeLabel ? <> · 판돈 <b>{sizeLabel}</b></> : null}
      </p>
      <p className="text-[10px] text-amber-200/70 leading-snug mt-1.5">제안일 뿐 매매 지시 아님 · 이미 들어갔다면 추가 진입이 아니라 같은 자리에 신호가 더 쌓였다는 뜻이에요.</p>
    </div>
  );
}

// 🎯 매수 후보 카드 — 대기는 한 줄 잠김, 발동/오늘발동분은 자동 펼침(07-16 디자인 v2). 클릭으로 토글.
// merged=병합 배너에 흡수됨(접힘 강등, 테두리만 유지) · solo=단독 발동(카드 첫 줄에 행동 문장 한 줄).
function SetupCard({ s, merged = false, solo = false }: { s: SniperSetup; merged?: boolean; solo?: boolean }) {
  const t = s.track;
  const lit = s.status === 'active';
  const fired = s.status === 'fired';
  const thinSample = t.n > 0 && t.n < 5;   // ③ 소표본 정직 — 표본 5건 미만이면 승률·기대값 숨김
  // 병합 배너에 흡수된 카드는 접어 강등(테두리 유지). 그 외엔 발동/오늘발동분 + 🌅아침 줍줍 자동 펼침.
  const [open, setOpen] = useState(merged ? false : (lit || fired || s.id === 'absorb'));
  // 🫳 단독 발동 카드의 행동 문장 — 진행 중 발동(시한 안) 하나를 문장으로(숫자 흩어놓지 않음)
  const soloFire = (lit && solo) ? s.fires.find((f) => f.deadline_ts && f.deadline_ts > Date.now() / 1000 && f.entry_px) : undefined;
  const dirTxt = s.dir > 0 ? '레버리지' : s.dir < 0 ? '인버스' : '양방향';
  const dirCls = s.dir > 0 ? 'text-red-200' : s.dir < 0 ? 'text-blue-200' : 'text-gray-300';
  const evCls = (t.ev ?? 0) > 0 ? 'text-red-300' : (t.ev ?? 0) < 0 ? 'text-blue-300' : 'text-gray-400';
  const winCls = t.win != null && t.win >= 55 ? 'text-red-300' : 'text-gray-300';
  const sizeCls = (s.size?.mult ?? 1) >= 1.4 ? 'text-red-300' : (s.size?.mult ?? 1) >= 1.1 ? 'text-red-200' : (s.size?.mult ?? 1) >= 0.9 ? 'text-gray-300' : 'text-blue-200';
  return (
    <div className={`rounded-xl border transition-all ${lit ? 'border-amber-400/80 bg-amber-500/[0.09] ring-1 ring-amber-400/40'
      : fired ? 'border-gray-700 bg-gray-900/50' : 'border-gray-800 bg-gray-900/30'}`}>
      {/* 한 줄 요약 — 항상 보임, 클릭하면 펼침 */}
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-x-2 gap-y-0.5 flex-wrap px-3 py-2 text-left">
        <b className="text-[13px] text-gray-100">{s.icon} {s.label}</b>
        {/* ⛏️ 검증 중 표시 — 스스로 찾은 자리라 실전 성적이 없다는 걸 첫눈에. 숨기면 소리 켜짐 판정 카드와 같아 보인다. */}
        {s.prob_card && <span className="text-[9.5px] px-1.5 py-px rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-300"
          title="시스템이 과거 기록에서 스스로 찾아낸 자리예요. 실전 성적이 쌓일 때까지는 투자 크기를 평소대로 두고, 못하면 자동으로 물러납니다.">검증 중</span>}
        {s.muted && <span className="text-[9.5px] text-gray-600" title="성적이 기준에 못 미쳐 알림은 멈췄어요. 되살아나는지 보려고 기록·채점은 계속합니다.">알림 멈춤</span>}
        {lit && <span className="text-[10px] px-1.5 py-px rounded-full border border-amber-500/50 bg-amber-500/20 text-amber-200 font-bold animate-pulse">지금 발동</span>}
        {!lit && fired && <span className="text-[10px] text-gray-500">오늘 {s.n_today}번</span>}
        {t.n === 0
          ? <span className="text-[10.5px] text-gray-600">표본 쌓는 중</span>
          : thinSample
            ? <span className="text-[10.5px] text-gray-500" title="표본이 너무 적어 승률은 아직 의미가 없어요.">{t.n}건 기록 중</span>
            : <span className="text-[10.5px] text-gray-400" title={t.kind === '실현'
                ? '실채점 — 발동 순간 정해둔 손절·목표에 실제로 어느 쪽이 먼저 닿았는지로 매긴 성적(왕복비용 차감)'
                : '추정 — 지수가 예상 방향으로 갔는지만 본 근사치. 실제 결판 표본이 차면 실채점으로 바뀝니다'}>
                {t.n}건 승률 <b className={winCls}>{t.win}%</b>
                <span className={`ml-1 text-[9px] ${t.kind === '실현' ? 'text-green-400/70' : 'text-gray-600'}`}>{t.kind === '실현' ? '실채점' : '추정'}</span>
              </span>}
        {s.size?.mult != null && <span className="text-[10.5px] text-gray-500">투자 크기 <b className={sizeCls}>{s.size.label}</b> ×{s.size.mult}</span>}
        <span className="ml-auto text-[10px] text-gray-600">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-3 pb-2.5 -mt-0.5 space-y-1">
          {/* 🫳 단독 발동 행동 문장 — "무엇을 하라"를 첫 줄에 문장으로(배너 없는 1건일 때) */}
          {soloFire && (
            <p className="text-[11px] text-amber-100 font-medium leading-snug bg-amber-500/10 border border-amber-400/30 rounded-lg px-2 py-1.5">
              🫳 지금 {dirAct(s.dir)} — 진입 <b className="font-mono">{fmtWon(soloFire.entry_px ?? 0)}원</b> · 손절 <b className="font-mono">{fmtWon(soloFire.stop_px ?? 0)}</b>(−0.5%) · 목표 <b className="font-mono">{fmtWon(soloFire.target_px ?? 0)}</b>(+1%) · 30분 안 결판{s.size?.label ? ` · 판돈 ${s.size.label}` : ''}
            </p>
          )}
          <div className="flex items-center gap-x-3 flex-wrap text-[10.5px]">
            <span className={dirCls}>{dirTxt}</span>
            {t.n > 0 && t.mfe != null && <span className="text-gray-500">최대 이익 폭 <b className="text-red-300/80">+{t.mfe}%p</b></span>}
            {t.n > 0 && t.mae != null && <span className="text-gray-500">최대 손실 폭 <b className="text-blue-300/80">{t.mae}%p</b></span>}
            {t.n > 0 && !thinSample && t.ev != null && (
              <span className="ml-auto" title="기대값 = 승률×목표 − 역률×손절. '실현'=실제 성적, '추정'=방향 실측 기반">
                기대값 <b className={`font-mono ${evCls}`}>{t.ev > 0 ? '+' : ''}{t.ev}%</b> <span className="text-gray-600">({t.kind})</span>
              </span>
            )}
          </div>
          <p className="text-[10.5px] text-gray-500 leading-snug">{s.cond}</p>
          {/* ⏱ 실채점 진행 — 표본이 차기 전엔 '추정'이 대표, 실제 결판 건수를 정직하게 병기(07-18) */}
          {t.kind === '추정' && (t.real?.n ?? 0) > 0 && (
            <p className="text-[9.5px] text-amber-300/70" title="발동 순간의 손절·목표에 실제로 닿았는지로 매긴 성적입니다. 표본이 차면 이 값이 카드의 대표 성적이 됩니다.">
              ⏱ 실제 결판 {t.real!.n}건{(t.real!.n >= 5 && t.real!.win != null) ? `(승률 ${t.real!.win}%)` : ''} — {t.real_need ?? 8}건부터 대표 성적으로 승격
            </p>
          )}
          {t.kind === '실현' && (t.cut_n ?? 0) > 0 && (
            <p className="text-[9.5px] text-gray-600">※ {t.cut_n}건은 시세가 끊겨 마지막 값으로 끊어 채점</p>
          )}
          {s.size?.why && <p className="text-[9.5px] text-gray-600" title={s.size.why}>🎰 {s.size.why}</p>}
          {s.fires.filter((f) => f.entry_px).slice(0, 2).map((f) => (
            <div key={f.id || f.ts} className="flex items-center gap-2 flex-wrap text-[10.5px] border-t border-gray-800/60 pt-1">
              <span className="text-gray-500 font-mono">{f.hm}</span>
              <span className={f.dir > 0 ? 'text-red-200' : 'text-blue-200'}>{f.inst_name}</span>
              <span className="text-gray-400">진입 <b className="font-mono text-gray-200">{f.entry_px?.toLocaleString('ko-KR')}</b></span>
              <span className="text-blue-300/70">손절 <b className="font-mono">{f.stop_px?.toLocaleString('ko-KR')}</b></span>
              <span className="text-green-300/70">목표 <b className="font-mono">{f.target_px?.toLocaleString('ko-KR')}</b></span>
            </div>
          ))}
          {s.note && <p className="text-[9.5px] text-gray-600">{s.note}</p>}
        </div>
      )}
    </div>
  );
}

// ⛏ 검증 중 선반(07-20) — 검증 중 카드 여러 장을 개별 나열 대신 한 줄로 접어 노란 상자 도배를 막는다.
// 발동 중인 검증 중 건은 ①의 행동 배너 집계엔 그대로 포함(여긴 표시만 강등). 클릭하면 지금의 카드들이 펼쳐짐.
function ProbShelf({ probs, mergedIds, soloIds, defaultOpen }: {
  probs: SniperSetup[]; mergedIds: Set<string>; soloIds: Set<string>; defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const firedN = probs.reduce((a, s) => a + (s.n_today || 0), 0);
  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <span className="text-[12px] font-semibold text-gray-300">⛏ 검증 중인 자리 {probs.length}개</span>
        <span className="text-[10px] text-gray-500">오늘 발동 {firedN} · 검증 중</span>
        <span className="ml-auto text-[11px] text-gray-500">{open ? '▲' : `▼ ${probs.length}개`}</span>
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1.5">
          {probs.map((s) => <SetupCard key={s.id} s={s} merged={mergedIds.has(s.id)} solo={soloIds.has(s.id)} />)}
        </div>
      )}
    </div>
  );
}

// 07-16 개편: 뉴단타 탭 해체 → 옛 조종 화면(section='board': 밤새 보유 판단·옛 매수 후보 자리·활성 매매 제안서)과
// 📊통계(section='ledger': 조합 발굴 후보·성적·이력)로 분해. 데이터 폴링·채점 계약은 불변(§5 무손상).
export default function TicketPage({ section = 'all', embedded = false }: { section?: 'board' | 'ledger' | 'all'; embedded?: boolean } = {}) {
  const [data, setData] = useState<TicketsResponse | null>(null);
  const [sniper, setSniper] = useState<SniperResponse | null>(null);
  const [mine, setMine] = useState<SniperMineResponse | null>(null);
  const [mineOpen, setMineOpen] = useState(false);
  const [err, setErr] = useState(false);
  const [pushOn, setPushOn] = useState<boolean>(() => getPushPref().ticket);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetchTickets().then((d) => { if (alive) { setData(d); setErr(false); } }).catch(() => { if (alive) setErr(true); });
      fetchSniper().then((d) => { if (alive) setSniper(d); }).catch(() => { /* 기록 장부 실패는 매매 제안서 화면 유지 */ });
    };
    load();
    fetchSniperMine().then((d) => { if (alive) setMine(d); }).catch(() => { /* 조합 발굴 후보는 선택 표시 */ });
    const i = setInterval(load, 15000);
    return () => { alive = false; clearInterval(i); };
  }, []);
  const mineCands = mine?.candidates ?? [];
  const regCount = mineCands.filter((c) => c.grade === '등재후보').length;
  const showBoard = section !== 'ledger';
  const showLedger = section !== 'board';

  // 🫳 행동 배너 집계(07-20) — 진행 중 발동(status active·시한 안)을 방향(=탈것)별로 묶는다.
  //   같은 방향 2건↑ = 배너 1개로 병합(멤버 카드는 접힘 강등) · 1건 = 카드에 행동 문장 · 양방향 동시 = 관망 경고.
  const nowS = Date.now() / 1000;
  // 진행 중 = 시한 안(deadline_ts>now). 카드 '지금 발동' 배지와 같은 기준 — 채점 완료돼도 시한 전이면 켜짐 유지.
  const activeFires: { f: SniperFire; s: SniperSetup }[] = [];
  for (const st of sniper?.setups ?? []) {
    for (const f of st.fires) {
      if (f.deadline_ts && f.deadline_ts > nowS && f.entry_px) activeFires.push({ f, s: st });
    }
  }
  const upG = activeFires.filter((a) => (a.f.dir ?? 0) > 0);   // 레버(반등 베팅)
  const dnG = activeFires.filter((a) => (a.f.dir ?? 0) < 0);   // 인버스(하락 베팅)
  const crossed = upG.length > 0 && dnG.length > 0;            // 방향 엇갈림 — 관망 권장
  const dirGroups = [dnG, upG].filter((g) => g.length > 0);
  const mergedIds = new Set<string>();                         // 병합 배너에 흡수된 setup(접힘)
  const soloIds = new Set<string>();                           // 단독 발동 setup(행동 문장)
  for (const g of dirGroups) {
    if (g.length >= 2) g.forEach((a) => mergedIds.add(a.s.id));
    else soloIds.add(g[0].s.id);
  }
  const bannerOf = (g: { f: SniperFire; s: SniperSetup }[]) => {
    const first = [...g].sort((a, b) => (a.f.ts ?? 0) - (b.f.ts ?? 0))[0];   // 첫 발동 건의 숫자 사용
    return { dir: first.f.dir ?? 0, count: g.length, fire: first.f, sizeLabel: first.s.size?.label };
  };
  const banners = dirGroups.filter((g) => g.length >= 2).map(bannerOf);
  const licensed = (sniper?.setups ?? []).filter((s) => s.kind !== 'prob');   // 소리 켜짐 3종 — 항상 개별
  const probs = (sniper?.setups ?? []).filter((s) => s.kind === 'prob');      // 검증 중 — 선반 한 줄로
  const probSoloOpen = probs.some((s) => soloIds.has(s.id));   // 검증 중 카드가 단독 발동이면 선반 자동 펼침

  const s = data?.stats;
  const active = data?.active ?? null;
  const history = [...(data?.today ?? []), ...(data?.today_h ?? [])]
    .filter((t) => t.status !== 'active')
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));

  return (
    <div className={embedded ? '' : 'flex-1 overflow-auto'}>
      <div className={embedded ? 'p-3 space-y-2' : 'max-w-2xl mx-auto p-3 sm:p-5 space-y-3'}>
        {showBoard && (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-[13px] font-bold text-gray-100">🫳 줍줍 자리 <span className="text-[10px] font-medium text-amber-300/90 align-middle ml-0.5 px-1.5 py-0.5 rounded border border-amber-500/40">잠정 β</span></h2>
            {sniper && sniper.live && !sniper.circuit && <span className="text-[10px] px-1.5 py-px rounded-full border border-amber-500/50 bg-amber-500/20 text-amber-200 font-bold animate-pulse">지금 발동</span>}
            {sniper && <span className="text-[10px] text-gray-600">사이렌 {sniper.budget.used}/{sniper.budget.max}발</span>}
            {/* 🔔 매매 제안서 푸시(이 기기) — 20분 시한부라 알림 없으면 놓침. opt-in: 여기서 켠 기기만 수신(관리자 전용) */}
            <button onClick={() => { const nv = !pushOn; setPushPref('ticket', nv); setPushOn(nv); }}
              className={`ml-auto text-[11px] px-2 py-1 rounded-lg border transition-colors ${pushOn
                ? 'border-amber-500/60 bg-amber-500/15 text-amber-200'
                : 'border-gray-700 bg-gray-800/60 text-gray-400'}`}
              title="티켓 발행·결과를 이 기기의 잠금화면 푸시로 받아요 (브라우저 알림 허용 + 🔔 푸시 구독이 먼저 켜져 있어야 함)">
              {pushOn ? '🔔 푸시 켬' : '🔕 푸시 꺼짐'}
            </button>
          </div>
          <p className="text-[10px] text-gray-600 leading-snug mt-0.5">확률이 뚜렷이 기운 순간만 카드로 알려요 — 제안일 뿐 매매 지시 아님, 성적 쌓기 전엔 관찰 권장.</p>
        </div>
        )}

        {/* 🌙 밤새 보유 판단 — 오늘 종가에 들고 자도 될지(마감 후 상시, 장중엔 pending 숨김) */}
        {showBoard && sniper?.gate && sniper.gate.signal !== 'pending' && (() => {
          const g = sniper.gate!;
          const c = g.signal === 'green' ? { b: 'border-green-500/50 bg-green-500/10', t: 'text-green-200' }
            : g.signal === 'red' ? { b: 'border-red-500/50 bg-red-500/10', t: 'text-red-200' }
              : { b: 'border-amber-500/40 bg-amber-500/10', t: 'text-amber-200' };
          return (
            <div className={`rounded-xl border ${c.b} px-3 py-2`}>
              <div className="flex items-center gap-2 flex-wrap">
                <b className={`text-[13px] ${c.t}`}>{g.title}</b>
                {g.prob != null && <span className="text-[10px] text-gray-400" title="5년 코스피 실측 — 이 마감 꼴 다음날 아침 시가가 오른 빈도">익일 아침 상승 실측 {g.prob}%</span>}
                <span className="ml-auto text-[10px] text-gray-600">🌙 밤새 보유 판단 · {g.as_of}</span>
              </div>
              {g.why.map((w, i) => <p key={i} className={`text-[10.5px] leading-snug mt-0.5 ${i === 0 ? c.t : 'text-gray-500'}`}>{w}</p>)}
              {g.exp_close && (
                <p className="text-[10.5px] text-gray-300 mt-1" title="장 마감 동시호가(15:20~15:30) 예상체결가 — 지금 이대로 끝나면">
                  📉 지금 이대로면 마감: 하이닉스 <b className="font-mono">{g.exp_close.price.toLocaleString('ko-KR')}원</b>{' '}
                  <b className={`font-mono ${g.exp_close.chg >= 0 ? 'text-red-300' : 'text-blue-300'}`}>({g.exp_close.chg >= 0 ? '+' : ''}{g.exp_close.chg}%)</b>
                </p>
              )}
              {g.tomorrow && g.tomorrow.events.length > 0 && <p className="text-[10px] text-amber-300/80 mt-1">📅 내일: {g.tomorrow.events.join(' · ')}</p>}
              {g.track && (
                <p className="text-[10px] text-gray-500 mt-1 pt-1 border-t border-gray-700/50">
                  {g.track.n > 0 && g.track.win != null
                    ? <>이 판단의 실제 성적: <b className="text-gray-300">{g.track.n}건 중 {g.track.win}% 적중</b> <span className="text-gray-600">(다음날 아침 시가로 채점)</span></>
                    : <span className="text-gray-600">이 판단의 실제 성적: 성적 쌓는 중 (다음날 아침 시가로 채점)</span>}
                </p>
              )}
            </div>
          );
        })()}

        {/* 매수 후보 카드들 — 헤더는 위로 통합됨(중복 제거 07-16) */}
        {showBoard && sniper && sniper.setups.length > 0 && (
          <div className="space-y-1.5">
            {/* 🛑 오늘 그만 — 당일 2연패로 서킷 발동 시 배너 + 카드 회색(무리한 만회 차단) */}
            {sniper.circuit && (
              <div className="rounded-xl border border-red-500/50 bg-red-500/10 px-3 py-2">
                <p className="text-[12px] font-bold text-red-200">🛑 오늘 그만</p>
                <p className="text-[10.5px] text-red-200/80 leading-snug mt-0.5">
                  오늘 줍줍이 2번 연속 빗나갔어요. 시장이 우리 패턴 밖에서 노는 날일 수 있어 <b>새 줍줍 알림을 멈췄습니다</b>(내일 자동 재개).
                  잃은 날 무리한 만회가 제일 위험해요 — 오늘은 쉬는 게 버는 겁니다.
                </p>
              </div>
            )}
            <div className={sniper.circuit ? 'space-y-2 opacity-45 grayscale pointer-events-none' : 'space-y-2'}>
              {/* 🫳 행동 배너 — 같은 방향 2건↑ 겹침. 양방향 동시면 관망 경고 먼저. */}
              {crossed && (
                <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 px-3 py-2">
                  <p className="text-[12px] font-bold text-amber-200">⚠️ 신호가 엇갈림 — 관망 권장</p>
                  <p className="text-[10.5px] text-amber-200/80 leading-snug mt-0.5">지금 오르는 쪽과 내리는 쪽 신호가 동시에 떠 있어요. 방향이 분명해질 때까지 기다리는 게 안전합니다.</p>
                </div>
              )}
              {banners.map((b) => <ActionBanner key={b.dir} dir={b.dir} count={b.count} fire={b.fire} sizeLabel={b.sizeLabel} />)}
              {/* 소리 켜짐 판정 카드 3장 — 항상 개별 노출(1급 시민) */}
              {licensed.map((s) => <SetupCard key={s.id} s={s} merged={mergedIds.has(s.id)} solo={soloIds.has(s.id)} />)}
              {/* ⛏ 검증 중 선반 — 여러 장을 한 줄로 접음(발동 건은 위 배너 집계에 이미 포함) */}
              {probs.length > 0 && <ProbShelf probs={probs} mergedIds={mergedIds} soloIds={soloIds} defaultOpen={probSoloOpen} />}
            </div>
          </div>
        )}

        {/* 🔎 조합 발굴 후보 — 시세 기록이 밤마다 스스로 찾아낸 새 옛 매수 후보 자리 제안(승인 대기) */}
        {showLedger && mineCands.length > 0 && (
          <div className="rounded-xl border border-gray-800 bg-gray-900/30">
            <button onClick={() => setMineOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
              <span className="text-[12px] font-semibold text-gray-300">🔎 자동 발굴 후보</span>
              <span className="text-[10px] text-gray-500">시스템이 스스로 새로 찾은 줍줍 자리 후보 — 토요일 승인 대기</span>
              {regCount > 0 && <span className="text-[10px] px-1.5 py-px rounded-full border border-teal-500/40 bg-teal-500/10 text-teal-300">등재후보 {regCount}</span>}
              <span className="ml-auto text-[11px] text-gray-500">{mineOpen ? '▲' : `▼ ${mineCands.length}개`}</span>
            </button>
            {mineOpen && (
              <div className="px-3 pb-3 space-y-1.5">
                <p className="text-[10px] text-gray-600 leading-snug">
                  학습 {mine?.train_days?.length ?? 0}일로 발굴 → 검증 {mine?.test_days?.length ?? 0}일로 확인(미래 훔쳐보기 방지).
                  <b className="text-gray-400"> n틱</b>은 30초 조각 수, <b className="text-gray-400">장면</b>이 진짜 표본(연속 조각은 겹침)이에요.
                  <b className="text-amber-300/80"> 자동 등재 안 함</b> — 사람이 승인해야 목록에 올라갑니다.
                </p>
                {mineCands.map((c) => {
                  const up = c.dir > 0;
                  return (
                    <div key={c.key} className="rounded-lg border border-gray-800 bg-gray-900/40 px-2.5 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[11px] px-1.5 py-px rounded-full font-medium ${c.grade === '등재후보' ? 'border border-teal-500/40 bg-teal-500/10 text-teal-300' : 'border border-gray-700 text-gray-500'}`}>{c.grade}</span>
                        <b className="text-[12px] text-gray-200">{c.label}</b>
                        <span className={`text-[12px] font-mono font-bold ${up ? 'text-red-300' : 'text-blue-300'}`}>{up ? '📈 상방' : '📉 하방'} {c.prob}%</span>
                      </div>
                      <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-0.5 text-[10px] text-gray-500">
                        <span>기준선 {c.base}% → <b className="text-gray-300">엣지 +{c.edge}%p</b></span>
                        <span>{c.n}틱 · <b className="text-gray-400">{c.ep}장면</b></span>
                        <span>평균 {c.avg_move > 0 ? '+' : ''}{c.avg_move}%p</span>
                        <span title="검증일(미래)에서도 같은 방향으로 유지됐나">검증: {c.wf === '검증' ? <b className="text-green-400/80">✅ {c.test_prob}%</b> : c.wf === '표본부족' ? <span className="text-gray-600">표본부족</span> : <span className="text-red-400/70">탈락</span>}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 오늘 성적 — 엔진의 신뢰 잔고 */}
        {showLedger && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 px-3 py-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-[12px]">
          <span className="text-gray-500">오늘 엔진 성적</span>
          {s && s.n > 0 ? (
            <>
              <span className="text-gray-200 font-semibold">{s.win}승 {s.loss}패{s.timeout > 0 ? ` ${s.timeout}무` : ''}</span>
              {s.win_rate != null && <span className="text-gray-400">승률 {s.win_rate}%</span>}
              <span className={`font-mono font-bold ${s.sum_pnl > 0 ? 'text-red-300' : s.sum_pnl < 0 ? 'text-blue-300' : 'text-gray-400'}`}>합계 {s.sum_pnl > 0 ? '+' : ''}{s.sum_pnl}%</span>
            </>
          ) : <span className="text-gray-600">아직 완료된 티켓 없음</span>}
          {data?.stats_h && data.stats_h.n > 0 && (
            <span className="text-[11px] text-amber-200/90">🏎 하이닉스 트랙 {data.stats_h.win}승 {data.stats_h.loss}패{data.stats_h.timeout > 0 ? ` ${data.stats_h.timeout}무` : ''} · <b className={`font-mono ${data.stats_h.sum_pnl > 0 ? 'text-red-300' : data.stats_h.sum_pnl < 0 ? 'text-blue-300' : 'text-gray-400'}`}>{data.stats_h.sum_pnl > 0 ? '+' : ''}{data.stats_h.sum_pnl}%</b></span>
          )}
          {data?.cum && data.cum.n > (s?.n ?? 0) && (
            <span className="text-[11px] text-gray-500">📊 누적({data.cum.days}일): {data.cum.n}장 · 승률 {data.cum.win_rate}% · <b className={`font-mono ${data.cum.sum_pnl > 0 ? 'text-red-300' : data.cum.sum_pnl < 0 ? 'text-blue-300' : 'text-gray-400'}`}>{data.cum.sum_pnl > 0 ? '+' : ''}{data.cum.sum_pnl}%</b></span>
          )}
          <span className="ml-auto text-[10px] text-gray-600">오늘 한도 {data?.cap ?? 6}장(변동성 연동) · 한 번에 1장</span>
        </div>
        )}

        {/* 활성 매매 제안서 — 발동 시에만(빈 '대기 중' 상자 제거 07-16, 대기는 위 카드 한 줄이 대신함) */}
        {showBoard && active && <TicketCard t={active} live />}

        {/* 🏎 하이닉스 병행 매매 제안서 — 같은 신호, 탈것만 사용자 실물(병행 채점 중) */}
        {showBoard && data?.active_h && (
          <div className="space-y-1">
            <p className="text-[11px] text-amber-200/90 font-medium">🏎 같은 신호 — 하이닉스 탈것 (병행 채점)</p>
            <TicketCard t={data.active_h} live />
          </div>
        )}

        {/* 오늘 이력 */}
        {showLedger && history.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] text-gray-500 font-medium">오늘 완료된 티켓</p>
            {history.map((t) => <TicketCard key={t.id} t={t} live={false} />)}
          </div>
        )}

        {err && <p className="text-[11px] text-red-300/80">서버 연결이 잠시 불안정해요 — 15초 뒤 자동 재시도.</p>}

        {showLedger && (
        <p className="text-[10px] text-gray-600 leading-snug border-t border-gray-800/60 pt-2">
          동작 원리: 🚩 국면 힘 세기가 강함(±40)을 1분 넘게 유지하고 국면(반등실패·역설 등)과 역행하지 않을 때,
          레버리지/인버스 ETF 티켓을 발행 — 손절 −0.5% · 목표 +1.0%(손익비 1:2) · 20분 시간제한. 한도는 변동성 연동(평온 6장 · 큰 날 최대 14장).
          모든 발행 건은 실시간 가격으로 자동 채점되고(승/패/시간초과·최대 이익 폭/최대 손실 폭), 성적은 숨기지 않아요.
          ⚠️ 정직 고지: 이 기능의 계산 방식(국면 힘 세기·국면 판별)도 아직 검증 중이에요 — 매일 밤 성적 판정에서 이긴 판을 쓰고, 이 성적표가 최종 심판입니다.
        </p>
        )}
      </div>
    </div>
  );
}
